import { useEffect, useState } from "react";
import { PAGE_SIZE, hasMore } from "../lib/consoleApi";
import { useAsync } from "./useAsync";

export interface PagedResult<T> {
  rows: T[];
  page: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  next: () => void;
  prev: () => void;
  reload: () => void;
}

/**
 * Offset pagination over a page-fetching function. Built on useAsync so the
 * loading/error/cancellation machinery lives in one place. Resets to page 0
 * whenever `deps` change.
 */
export function usePaged<T>(fetchPage: (offset: number) => Promise<T[]>, deps: unknown[]): PagedResult<T> {
  const [page, setPage] = useState(0);
  // Reset to page 0 whenever deps change.
  useEffect(() => { setPage(0); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, loading, error, reload } = useAsync(() => fetchPage(page * PAGE_SIZE), [page, ...deps]);
  const rows = data ?? [];
  return {
    rows, page, loading, error,
    hasMore: hasMore(rows, PAGE_SIZE),
    next: () => setPage((p) => p + 1),
    prev: () => setPage((p) => Math.max(0, p - 1)),
    reload
  };
}
